import React, { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import './MonitGPS.css';

const ScrollReveal = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Zawodnik = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('/api/send_order.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setStatus('error');
        setErrorMessage('Wystąpił błąd podczas wysyłania zamówienia. Prosimy skontaktować się telefonicznie.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Wystąpił problem z połączeniem lub serwerem PHP.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav
        className="fixed top-0 left-0 right-0 z-50 glass-effect"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <a href="/">
              <img
                src="/images/logominitgps.png"
                alt="MonitGPS Logo"
                className="h-12 w-auto cursor-pointer"
              />
            </a>
          </div>

          <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
            <a href="/" className="hover:text-cyan-400 transition-colors">Strona Główna</a>
            <a href="/Elearning" target="_blank" rel="noreferrer" className="hover:text-cyan-400 transition-colors text-cyan-500 font-bold">Szkolenie E-learning</a>
          </div>
        </div>
      </motion.nav>

      {/* Hero Header */}
      <section className="relative pt-32 pb-16 flex items-center justify-center overflow-hidden bg-carbon min-h-[50vh]">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>

        <motion.div
          className="absolute top-1/2 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[100px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.h1
            className="font-display font-black text-5xl md:text-7xl leading-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Strefa <span className="text-gradient">Zawodnika</span>
          </motion.h1>

          <motion.p
            className="text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Wszystkie potrzebne materiały, instrukcje obsługi urządzeń oraz możliwość zamówienia zestawów montażowych w jednym miejscu.
          </motion.p>
          
          <motion.div 
            className="mt-10 flex justify-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <a href="/Elearning" target="_blank" rel="noreferrer" className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl font-display font-bold text-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all flex items-center space-x-3">
              <span>Otwórz obowiązkowe Szkolenie E-learning</span>
              <span className="text-2xl">🎓</span>
            </a>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50 scan-line"></div>
      </section>

      {/* PDF Downloads Section */}
      <section id="downloads" className="py-24 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                Do pobrania
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl mb-6">
                Instrukcje i <span className="text-gradient">materiały</span>
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8">
            <ScrollReveal delay={0.1}>
              <div className="glass-effect rounded-3xl p-8 hover-lift flex flex-col items-center text-center">
                <div className="text-6xl mb-6">📄</div>
                <h3 className="font-display font-bold text-2xl mb-4">Instrukcja Obsługi Urządzenia</h3>
                <p className="text-gray-400 mb-8 max-w-sm">
                  Szczegółowa instrukcja użytkowania trackerów MonitGPS przeznaczona dla zawodników i załóg rajdowych.
                </p>
                <a
                  href="/downloads/device-instruction.pdf"
                  download
                  target="_blank"
                  className="mt-auto px-8 py-4 glass-effect border border-cyan-500/30 rounded-2xl font-display font-bold text-cyan-400 hover:bg-cyan-500/10 transition-all w-full flex items-center justify-center space-x-2"
                >
                  <span>Pobierz PDF</span>
                  <span className="text-xl">⬇</span>
                </a>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="glass-effect rounded-3xl p-8 hover-lift flex flex-col items-center text-center">
                <div className="text-6xl mb-6">🔧</div>
                <h3 className="font-display font-bold text-2xl mb-4">Schemat Instalacji</h3>
                <p className="text-gray-400 mb-8 max-w-sm">
                  Instrukcja i schemat poprawnego montażu okablowania oraz mocowań dla urządzeń GPS w samochodzie rajdowym.
                </p>
                <a
                  href="/downloads/installation-manual.pdf"
                  download
                  target="_blank"
                  className="mt-auto px-8 py-4 glass-effect border border-blue-500/30 rounded-2xl font-display font-bold text-blue-400 hover:bg-blue-500/10 transition-all w-full flex items-center justify-center space-x-2"
                >
                  <span>Pobierz PDF</span>
                  <span className="text-xl">⬇</span>
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Order Installation Kit Section */}
      <section id="order-kit" className="py-24 px-6 relative bg-gradient-to-b from-transparent to-[#050505]">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                Wyposażenie pojazdu
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl mb-6">
                Zamów <span className="text-gradient">Zestaw / Wypożyczenie</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Zamów dedykowany zestaw do montażu urządzenia MonitGPS na stałe, lub zgłoś chęć wypożyczenia trackera na pojedynczy rajd.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Kit Details */}
            <ScrollReveal delay={0.2}>
              <div className="glass-effect rounded-3xl p-8 h-full flex flex-col relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>

                <div className="mb-6 relative z-10 flex flex-col space-y-4">
                  <div>
                    <h3 className="font-display font-bold text-2xl text-cyan-400 mb-2">Cennik dla Zawodnika:</h3>
                    <p className="text-gray-400 text-sm">Zgłoś chęć wypożyczenia urządzenia lub kup na własność zestaw montażowy.</p>
                  </div>
                  <div className="flex flex-row flex-wrap gap-3 w-full">
                    <div className="flex-1 min-w-[140px] bg-blue-500/10 border border-blue-500/20 px-4 py-3 rounded-xl text-center">
                      <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-1 font-bold">Wypożyczenie</span>
                      <strong className="text-2xl text-white font-display">150 zł</strong>
                      <span className="text-blue-400 ml-1 text-sm font-bold">/ rajd</span>
                    </div>
                    <div className="flex-1 min-w-[140px] bg-cyan-500/10 border border-cyan-500/20 px-4 py-3 rounded-xl text-center">
                      <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-1 font-bold">Cena Zestawu</span>
                      <strong className="text-2xl text-white font-display">350 zł</strong>
                      <span className="text-cyan-400 ml-1 text-sm font-bold">/ szt.</span>
                    </div>
                  </div>
                </div>

                {/* 3 Images Grid */}
                <div className="grid grid-cols-3 gap-3 mb-8 relative z-10">
                  <div className="aspect-square rounded-xl bg-black/40 border border-white/5 overflow-hidden flex items-center justify-center text-2xl">
                    <img src="/images/kit1.jpg" alt="Zestaw 1" className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="absolute z-[-1]">📷</span>
                  </div>
                  <div className="aspect-square rounded-xl bg-black/40 border border-white/5 overflow-hidden flex items-center justify-center text-2xl">
                    <img src="/images/kit2.jpg" alt="Zestaw 2" className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="absolute z-[-1]">📷</span>
                  </div>
                  <div className="aspect-square rounded-xl bg-black/40 border border-white/5 overflow-hidden flex items-center justify-center text-2xl">
                    <img src="/images/kit3.jpg" alt="Zestaw 3" className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="absolute z-[-1]">📷</span>
                  </div>
                </div>

                {/* Product List */}
                <div className="flex-grow flex flex-col justify-start relative z-10">
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-3 flex-shrink-0">✓</span>
                      <div>
                        <strong className="text-white block text-lg">Antena 2 w 1 (GPS, GPRS)</strong>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-3 mt-1 flex-shrink-0">✓</span>
                      <div>
                        <div className="flex items-center flex-wrap gap-3">
                          <strong className="text-white block text-lg">Uchwyty montażowe RAM</strong>
                          <div className="bg-white/10 px-2 py-1 rounded-md flex items-center justify-center">
                            <img src="/images/ram-logo.png" alt="RAM Mounts" className="h-5 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        </div>
                        <div className="text-cyan-400/80 text-sm mt-1">
                          <span className="font-medium block">Oryginalne elementy mocujące marki RAM:</span>
                          <ul className="list-[circle] list-inside mt-1 ml-1 text-gray-300">
                            <li>RAM-B-201U-C</li>
                            <li>RAM-B-347U-BT</li>
                          </ul>
                        </div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-3 flex-shrink-0">✓</span>
                      <div>
                        <strong className="text-white block text-lg">Kabel zasilający</strong>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            {/* Email Order Form */}
            <ScrollReveal delay={0.4}>
              <div className="glass-effect rounded-3xl p-8 relative">
                <h3 className="font-display font-bold text-3xl mb-6 text-white text-center">
                  Formularz Zamówienia
                </h3>

                {status === 'success' ? (
                  <div className="flex flex-col items-center justify-center text-center py-12 space-y-4">
                    <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-500/50 flex items-center justify-center text-green-400 text-4xl mb-4">✓</div>
                    <h4 className="text-3xl font-bold font-display text-white">Zamówienie przyjęte!</h4>
                    <p className="text-gray-400 mt-2">Dziękujemy, Twoje zamówienie zostało wysłane. Skontaktujemy się z Tobą wkrótce.</p>
                  </div>
                ) : (
                  <form className="space-y-5" onSubmit={handleSubmit}>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Imię i nazwisko / Nazwa zespołu</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white"
                      placeholder="Skoda Rally Team"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Adres Email</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white"
                        placeholder="kontakt@team.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Telefon Kontaktowy</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white"
                        placeholder="+48 123 456 789"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Dodatkowe uwagi (opcjonalnie)</label>
                    <textarea
                      rows="3"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white resize-none"
                      placeholder="Adres do wysyłki. Ilość sztuk."
                    ></textarea>
                  </div>

                  {errorMessage && (
                    <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 text-sm font-medium mt-4 mb-4">
                      {errorMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full py-4 mt-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl font-display font-bold text-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>{status === 'loading' ? 'Wysyłanie...' : 'Wyślij Zamówienie'}</span>
                    <span>✉️</span>
                  </button>
                  <p className="text-xs text-center text-gray-500 mt-3">
                    Kliknięcie przycisku wyśle zamówienie do nas. Skontaktujemy się z Tobą w celu potwierdzenia zamówienia.
                  </p>
                </form>
              )}
            </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Footer / Contact (Same as main page footer) */}
      <section id="contact" className="py-24 px-6 relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <ScrollReveal>
            <h2 className="font-display font-black text-4xl mb-8">Kontakt z działem technicznym</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="glass-effect rounded-2xl p-6">
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Maciek</div>
                <a href="tel:504178476" className="text-gray-300 hover:text-cyan-400 transition-colors">504 178 476</a>
              </div>
              <div className="glass-effect rounded-2xl p-6">
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Adrian</div>
                <a href="tel:791807484" className="text-gray-300 hover:text-cyan-400 transition-colors">791 807 484</a>
              </div>
              <div className="glass-effect rounded-2xl p-6">
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Dawid</div>
                <a href="tel:603197643" className="text-gray-300 hover:text-cyan-400 transition-colors">603 197 643</a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Zawodnik;
